// src/components/CamposCustomizados/CamposCustomizadosModal.js
// CRUD campo customizado: nome, id_tipo_documento (opcional), tipo_campo (Inteiro, Decimal, Data, Cadastro), situacao.
// Para tipo_campo = Cadastro: gerencia opções (tabela campos_customizados_opcoes) no mesmo modal.
import React, { useEffect, useState, useCallback, useContext, useRef, useMemo } from "react";
import { Modal, Button, Form, Row, Col, Table, Spinner } from "react-bootstrap";
import { FaTrash, FaArrowUp, FaArrowDown, FaSave } from "react-icons/fa";
import api, { BASE_URL } from "../../Utils/api";
import { GlobalContext } from "../../Utils/GlobalContext";
import { useFeedback } from "../Generals/Feedback/FeedbackContext";
import TiposDocumentoModal from "../TiposDocumento/TiposDocumentoModal";
import "./CamposCustomizadosModal.css";


const TIPOS_CAMPO = [
  { value: "Inteiro", label: "Inteiro" },
  { value: "Decimal", label: "Decimal" },
  { value: "Data", label: "Data" },
  { value: "Cadastro", label: "Cadastro (opções fixas: ex. Azul, Verde, Amarelo)" },
];

const CamposCustomizadosModal = ({ show, handleClose, campo, entidadeId, fetchCampos }) => {
  const { confirm } = useFeedback();
  const { globalData } = useContext(GlobalContext);
  const userId = globalData?.user_id || localStorage.getItem("user_id");

  const [formValues, setFormValues] = useState({
    nome: "",
    id_tipo_documento: "",
    id_tipos_documento: [],
    tipo_campo: "Inteiro",
    situacao: "A",
    visivel: "S",
    obrigatorio: "N",
    identifica_pessoa: "N",
  });
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [opcoes, setOpcoes] = useState([]);
  const [opcaoNova, setOpcaoNova] = useState({ valor: "", ordem: 0, situacao: "A" });
  const [saving, setSaving] = useState(false);
  const [savingOpcaoId, setSavingOpcaoId] = useState(null);
  const [error, setError] = useState(null);
  const [tiposDocumentoOpen, setTiposDocumentoOpen] = useState(false);
  const [showTipoDocumentoModal, setShowTipoDocumentoModal] = useState(false);
  const idsTiposDocumentoAntesCadastroRef = useRef(new Set());
  // Após criar novo campo, mantém o modal aberto para permitir cadastrar opções (tipo Cadastro)
  const [savedCampoRef, setSavedCampoRef] = useState(null);

  const effectiveCampo = campo || savedCampoRef;

  const carregarTiposDocumento = useCallback(async (idTipoDocumentoPreferencial = null, selecionarCriado = false) => {
    if (!entidadeId) return [];
    try {
      const { data } = await api.get(
        `${BASE_URL}/api/tiposDocumento/ListarTiposDocumento/${entidadeId}`
      );
      const lista = Array.isArray(data?.data) ? data.data : [];
      setTiposDocumento(lista);

      const tipoCriado = selecionarCriado
        ? lista.find((tipo) => !idsTiposDocumentoAntesCadastroRef.current.has(String(tipo.id)))
        : null;
      const idParaSelecionar = idTipoDocumentoPreferencial ?? tipoCriado?.id;

      if (idParaSelecionar != null) {
        const idStr = String(idParaSelecionar);
        setFormValues((prev) => ({
          ...prev,
          id_tipo_documento: idStr,
          id_tipos_documento: prev.id_tipos_documento.includes(idStr)
            ? prev.id_tipos_documento
            : [...prev.id_tipos_documento, idStr],
        }));
      }

      idsTiposDocumentoAntesCadastroRef.current = new Set(
        lista.map((tipo) => String(tipo.id))
      );
      return lista;
    } catch (err) {
      console.error("Erro ao buscar tipos de documento:", err);
      return [];
    }
  }, [entidadeId]);

  const carregarOpcoes = useCallback(async (idCampo) => {
    if (!idCampo) return;
    try {
      const { data } = await api.get(
        `${BASE_URL}/api/camposCustomizados/ListarOpcoesCampo/${idCampo}`
      );
      const lista = Array.isArray(data?.data) ? data.data : [];
      lista.sort((a, b) => (Number(a.ordem) || 0) - (Number(b.ordem) || 0) || String(a.valor || "").localeCompare(String(b.valor || "")));
      const precisaNormalizar = lista.some((op, i) => (Number(op.ordem) || 0) !== i + 1);
      if (precisaNormalizar && lista.length > 0) {
        await Promise.all(
          lista.map((op, i) =>
            api.put(`${BASE_URL}/api/camposCustomizados/EditarOpcaoCampo/${op.id}`, {
              valor: op.valor,
              ordem: i + 1,
              situacao: op.situacao,
            })
          )
        );
        const { data: data2 } = await api.get(
          `${BASE_URL}/api/camposCustomizados/ListarOpcoesCampo/${idCampo}`
        );
        const lista2 = Array.isArray(data2?.data) ? data2.data : [];
        lista2.sort((a, b) => (Number(a.ordem) || 0) - (Number(b.ordem) || 0) || String(a.valor || "").localeCompare(String(b.valor || "")));
        setOpcoes(lista2);
      } else {
        setOpcoes(lista);
      }
    } catch (err) {
      console.error("Erro ao listar opções do campo:", err);
      setOpcoes([]);
    }
  }, []);

  const prevShowRef = useRef(false);

  useEffect(() => {
    if (show) carregarTiposDocumento();
  }, [show, carregarTiposDocumento]);

  // Quando o modal *abre* (transição false -> true): limpa savedCampoRef e, se for + Adicionar (campo null), reseta o formulário
  useEffect(() => {
    if (show && !prevShowRef.current) {
      prevShowRef.current = true;
      setSavedCampoRef(null);
      setError(null);
      if (campo == null) {
        setFormValues({ nome: "", id_tipo_documento: "", id_tipos_documento: [], tipo_campo: "Inteiro", situacao: "A", visivel: "S", obrigatorio: "N", identifica_pessoa: "N" });
        setOpcoes([]);
        setOpcaoNova({ valor: "", ordem: 0, situacao: "A" });
      }
    }
    if (!show) prevShowRef.current = false;
  }, [show, campo]);

  useEffect(() => {
    if (show) {
      const objeto = campo || savedCampoRef;
      console.log("[CamposCustomizadosModal] Objeto ao abrir:", objeto ?? "(novo)");
    }
  }, [show, campo, savedCampoRef]);

  // Sincroniza formulário com o campo em edição (quando abrimos para editar um registro existente)
  useEffect(() => {
    if (!show || !campo) return;
    setFormValues({
      nome: campo.descricao || campo.nome_campo || campo.nome || "",
      id_tipo_documento: campo.id_tipo_documento ?? "",
      id_tipos_documento: Array.isArray(campo.id_tipos_documento)
        ? campo.id_tipos_documento.map((id) => String(id))
        : (campo.id_tipo_documento ? [String(campo.id_tipo_documento)] : []),
      tipo_campo: campo.tipo_campo || "Inteiro",
      situacao: campo.situacao === "I" ? "I" : "A",
      visivel: campo.visivel === "N" ? "N" : "S",
      obrigatorio: campo.obrigatorio === "S" ? "S" : "N",
      identifica_pessoa: campo.identifica_pessoa === "S" ? "S" : "N",
    });
    if (campo.id && campo.tipo_campo === "Cadastro") carregarOpcoes(campo.id);
    else setOpcoes([]);
  }, [show, campo, carregarOpcoes]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const toggleTipoDocumento = (id) => {
    const idStr = String(id);
    setFormValues((prev) => ({
      ...prev,
      id_tipos_documento: prev.id_tipos_documento.includes(idStr)
        ? prev.id_tipos_documento.filter((item) => item !== idStr)
        : [...prev.id_tipos_documento, idStr],
    }));
  };

  const limparTiposDocumento = () => {
    setFormValues((prev) => ({
      ...prev,
      id_tipos_documento: [],
      id_tipo_documento: "",
    }));
  };

  const abrirCadastroTipoDocumento = () => {
    idsTiposDocumentoAntesCadastroRef.current = new Set(
      tiposDocumento.map((tipo) => String(tipo.id))
    );
    setShowTipoDocumentoModal(true);
  };

  const handleCloseTipoDocumentoModal = async (resultado) => {
    setShowTipoDocumentoModal(false);
    const idCriado = resultado?.id ?? resultado?.id_tipo_documento ?? resultado?.tipoDocumentoId;
    await carregarTiposDocumento(idCriado != null ? String(idCriado) : null, Boolean(resultado));
  };

  const tiposDocumentoResumo = useMemo(() => {
    if (!formValues.id_tipos_documento.length) return "Todos os tipos de documento";
    const nomes = formValues.id_tipos_documento
      .map((id) => tiposDocumento.find((tipo) => String(tipo.id) === String(id))?.nome)
      .filter(Boolean);
    if (nomes.length === 0) return `${formValues.id_tipos_documento.length} selecionado(s)`;
    if (nomes.length <= 2) return nomes.join(", ");
    return `${nomes.length} selecionados`;
  }, [formValues.id_tipos_documento, tiposDocumento]);

  const handleSaveCampo = async () => {
    setError(null);
    const nome = String(formValues.nome || "").trim();
    if (!nome) {
      setError("Nome do campo é obrigatório.");
      return;
    }
    setSaving(true);
    try {
      if (effectiveCampo?.id) {
        await api.put(
          `${BASE_URL}/api/camposCustomizados/EditarCampoCustomizado/${effectiveCampo.id}`,
          {
            nome,
            id_tipo_documento: formValues.id_tipos_documento.length === 1 ? formValues.id_tipos_documento[0] : null,
            id_tipos_documento: formValues.id_tipos_documento,
            tipo_campo: formValues.tipo_campo,
            situacao: formValues.situacao,
            visivel: formValues.visivel,
            obrigatorio: formValues.obrigatorio,
            identifica_pessoa: formValues.identifica_pessoa,
          }
        );
        fetchCampos();
        handleClose();
      } else {
        const { data } = await api.post(
          `${BASE_URL}/api/camposCustomizados/InserirCampoCustomizado/${entidadeId}`,
          {
            nome,
            id_tipo_documento: formValues.id_tipos_documento.length === 1 ? formValues.id_tipos_documento[0] : null,
            id_tipos_documento: formValues.id_tipos_documento,
            tipo_campo: formValues.tipo_campo,
            situacao: formValues.situacao,
            visivel: formValues.visivel,
            obrigatorio: formValues.obrigatorio,
            identifica_pessoa: formValues.identifica_pessoa,
            id_usuario_criacao: userId || null,
          }
        );
        const newId = data?.data?.id;
        fetchCampos();
        if (newId != null && formValues.tipo_campo === "Cadastro") {
          setSavedCampoRef({
            id: newId,
            nome,
            id_tipo_documento: formValues.id_tipos_documento.length === 1 ? formValues.id_tipos_documento[0] : null,
            id_tipos_documento: formValues.id_tipos_documento,
            tipo_campo: formValues.tipo_campo,
            situacao: formValues.situacao,
            visivel: formValues.visivel,
            obrigatorio: formValues.obrigatorio,
            identifica_pessoa: formValues.identifica_pessoa,
          });
          carregarOpcoes(newId);
        } else {
          handleClose();
        }
      }
    } catch (err) {
      console.error("Erro ao salvar campo customizado:", err);
      setError(err.response?.data?.message || "Erro ao salvar. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  const handleAddOpcao = async () => {
    setError(null);
    const valor = String(opcaoNova.valor || "").trim();
    if (!valor) {
      setError("Informe o valor da opção.");
      return;
    }
    if (!effectiveCampo?.id) return;
    try {
      await api.post(`${BASE_URL}/api/camposCustomizados/InserirOpcaoCampo`, {
        id_campo_customizado: effectiveCampo.id,
        valor,
        ordem: opcoes.length + 1,
        situacao: opcaoNova.situacao || "A",
      });
      setOpcaoNova({ valor: "", ordem: opcoes.length + 1, situacao: "A" });
      carregarOpcoes(effectiveCampo.id);
    } catch (err) {
      console.error("Erro ao inserir opção:", err);
      setError(err.response?.data?.message || "Erro ao adicionar opção.");
    }
  };

  const handleSaveOpcaoCampo = async (id, updates) => {
    if (!id) return;
    setSavingOpcaoId(id);
    try {
      await api.put(`${BASE_URL}/api/camposCustomizados/EditarOpcaoCampo/${id}`, updates);
      if (effectiveCampo?.id) carregarOpcoes(effectiveCampo.id);
    } catch (err) {
      console.error("Erro ao atualizar opção:", err);
      setError(err.response?.data?.message || "Erro ao salvar.");
    } finally {
      setSavingOpcaoId(null);
    }
  };

  const handleExcluirOpcao = async (id) => {
    const ok = await confirm("Excluir esta opção?");
    if (!ok) return;
    try {
      await api.delete(`${BASE_URL}/api/camposCustomizados/ExcluirOpcaoCampo/${id}`);
      if (effectiveCampo?.id) carregarOpcoes(effectiveCampo.id);
    } catch (err) {
      console.error("Erro ao excluir opção:", err);
      setError(err.response?.data?.message || "Erro ao excluir opção.");
    }
  };

  const moveOpcao = async (index, direction) => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= opcoes.length) return;
    const newOpcoes = [...opcoes];
    const a = newOpcoes[index];
    const b = newOpcoes[targetIndex];
    newOpcoes[index] = b;
    newOpcoes[targetIndex] = a;
    const ordemA = targetIndex + 1;
    const ordemB = index + 1;
    setSavingOpcaoId(a.id);
    try {
      await Promise.all([
        api.put(`${BASE_URL}/api/camposCustomizados/EditarOpcaoCampo/${a.id}`, { valor: a.valor, ordem: ordemA, situacao: a.situacao }),
        api.put(`${BASE_URL}/api/camposCustomizados/EditarOpcaoCampo/${b.id}`, { valor: b.valor, ordem: ordemB, situacao: b.situacao }),
      ]);
      if (effectiveCampo?.id) carregarOpcoes(effectiveCampo.id);
    } catch (err) {
      console.error("Erro ao reordenar opção:", err);
      setError(err.response?.data?.message || "Erro ao reordenar.");
    } finally {
      setSavingOpcaoId(null);
    }
  };

  const isCadastro = formValues.tipo_campo === "Cadastro";
  const mostraOpcoes = isCadastro && effectiveCampo?.id;
  const temRespostas = effectiveCampo?.tem_respostas === "S";
  const bloqueiaCamposPorRespostas = temRespostas;

  return (
    <>
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static" dialogClassName="modal-dialog-top">
      <Modal.Header closeButton style={{ background: "var(--surface-sunken)", borderColor: "var(--border-default)", paddingBottom: 12 }}>
        <Modal.Title style={{ fontSize: "1rem", fontWeight: 600, color: "var(--text-body)" }}>
          {effectiveCampo ? "Editar Campo Customizado" : "Novo Campo Customizado"}
          {(effectiveCampo?.descricao || effectiveCampo?.nome_campo) && (
            <span style={{ display: "block", fontSize: "0.82rem", fontWeight: 400, color: "var(--text-muted)", marginTop: 2 }}>
              {effectiveCampo.descricao || effectiveCampo.nome_campo}
            </span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ background: "var(--surface-card)", color: "var(--text-body)" }}>
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        <Form>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" data-tour="campos-customizados-nome">
                <Form.Label>Nome do campo <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  name="nome"
                  value={formValues.nome}
                  onChange={handleChange}
                  placeholder="Ex: Cor do veículo"
                  disabled={bloqueiaCamposPorRespostas}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3" data-tour="campos-customizados-tipo-documento">
                <Form.Label>Tipo de documento</Form.Label>
                <div className="ccm-select-add-combo">
                  <div className="position-relative ccm-select-add-combo-select">
                  <button
                    type="button"
                    className="form-control d-flex align-items-center justify-content-between text-left"
                    style={{
                      height: 38,
                      color: "var(--text-body)",
                      background: bloqueiaCamposPorRespostas ? "var(--surface-sunken)" : "var(--surface-card)",
                      borderColor: "var(--border-default)",
                      overflow: "hidden",
                    }}
                    disabled={bloqueiaCamposPorRespostas}
                    aria-expanded={tiposDocumentoOpen}
                    aria-controls="tipos-documento-checkbox-list"
                    onClick={() => setTiposDocumentoOpen((open) => !open)}
                  >
                    <span
                      style={{
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {tiposDocumentoResumo}
                    </span>
                    <span aria-hidden style={{ marginLeft: 8, flexShrink: 0 }}>
                      {tiposDocumentoOpen ? "^" : "v"}
                    </span>
                  </button>

                  {tiposDocumentoOpen && !bloqueiaCamposPorRespostas && (
                    <div
                      id="tipos-documento-checkbox-list"
                      className="position-absolute w-100 mt-1 shadow-sm"
                      style={{
                        zIndex: 1060,
                        maxHeight: 240,
                        overflowY: "auto",
                        background: "var(--surface-card)",
                        border: "1px solid var(--border-default)",
                        borderRadius: 4,
                      }}
                    >
                      <div
                        className="d-flex align-items-center justify-content-between"
                        style={{ padding: "8px 12px", borderBottom: "1px solid var(--border-subtle)" }}
                      >
                        <span className="text-muted small">Marque os tipos desejados</span>
                        {formValues.id_tipos_documento.length > 0 && (
                          <Button variant="link" size="sm" className="p-0" onClick={limparTiposDocumento}>
                            Limpar
                          </Button>
                        )}
                      </div>
                      {tiposDocumento.length === 0 ? (
                        <div className="text-muted small" style={{ padding: "10px 12px" }}>
                          Nenhum tipo de documento encontrado.
                        </div>
                      ) : (
                        tiposDocumento.map((tipo) => {
                          const id = String(tipo.id);
                          const checked = formValues.id_tipos_documento.includes(id);
                          return (
                            <label
                              key={tipo.id}
                              className="d-flex align-items-center mb-0"
                              style={{
                                gap: 8,
                                padding: "9px 12px",
                                cursor: "pointer",
                                borderBottom: "1px solid var(--border-subtle)",
                              }}
                            >
                              <Form.Check
                                type="checkbox"
                                id={`tipo-documento-campo-${tipo.id}`}
                                aria-label={`Selecionar ${tipo.nome}`}
                                checked={checked}
                                onChange={() => toggleTipoDocumento(tipo.id)}
                                label=""
                                className="mb-0"
                              />
                              <span style={{ lineHeight: 1.25 }}>{tipo.nome}</span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  )}
                  </div>
                  <span className="ccm-select-add-combo-button-wrapper">
                    <button
                      type="button"
                      className="ccm-select-add-combo-button"
                      onClick={abrirCadastroTipoDocumento}
                      title="Adicionar tipo de documento"
                      aria-label="Adicionar tipo de documento"
                      disabled={bloqueiaCamposPorRespostas}
                    >
                      <span className="ccm-select-add-action-circle" aria-hidden="true" />
                    </button>
                  </span>
                </div>
                <Form.Text className="text-muted">Nenhum selecionado = campo disponivel para qualquer tipo. Selecione um ou mais tipos para restringir.</Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" data-tour="campos-customizados-tipo-campo">
                <Form.Label>Tipo do campo</Form.Label>
                <Form.Select name="tipo_campo" value={formValues.tipo_campo} onChange={handleChange} disabled={bloqueiaCamposPorRespostas}>
                  {TIPOS_CAMPO.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Inteiro, Decimal, Data ou Cadastro (lista de opções: ex. Azul, Verde, Amarelo).
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3" data-tour="campos-customizados-ativo">
                <Form.Check
                  type="switch"
                  id="situacao-campo"
                  name="situacao"
                  label={formValues.situacao === "A" ? "Ativo" : "Inativo"}
                  checked={formValues.situacao === "A"}
                  onChange={() => {
                    setFormValues((p) => {
                      const novaSituacao = p.situacao === "A" ? "I" : "A";
                      if (novaSituacao === "I") {
                        return { ...p, situacao: "I", visivel: "N", obrigatorio: "N" };
                      }
                      return { ...p, situacao: "A" };
                    });
                  }}
                />
              </Form.Group>
              <Form.Group className="mb-3" data-tour="campos-customizados-visivel">
                <Form.Check
                  type="switch"
                  id="visivel-campo"
                  label="Visível"
                  checked={formValues.visivel === "S"}
                  onChange={() => {
                    setFormValues((p) => {
                      const visivel = p.visivel === "S" ? "N" : "S";
                      const obrigatorio = visivel === "N" ? "N" : p.obrigatorio;
                      return { ...p, visivel, obrigatorio };
                    });
                  }}
                />
              </Form.Group>
              <Form.Group className="mb-3" data-tour="campos-customizados-obrigatorio">
                <Form.Check
                  type="switch"
                  id="obrigatorio-campo"
                  label="Obrigatório"
                  checked={formValues.obrigatorio === "S"}
                  disabled={formValues.visivel === "N"}
                  onChange={() => setFormValues((p) => ({ ...p, obrigatorio: p.obrigatorio === "S" ? "N" : "S" }))}
                />
              </Form.Group>
              <Form.Group className="mb-3" data-tour="campos-customizados-dados-pessoais">
                <Form.Check
                  type="switch"
                  id="identifica-pessoa-campo"
                  label="Dado pessoal identificável"
                  checked={formValues.identifica_pessoa === "S"}
                  onChange={() =>
                    setFormValues((p) => ({ ...p, identifica_pessoa: p.identifica_pessoa === "S" ? "N" : "S" }))
                  }
                />
                <Form.Text className="text-muted">
                  Marque se o valor deste campo identifica uma pessoa (ex: nome, CPF, e-mail, telefone). Campos
                  marcados são mascarados nas exportações quando a proteção de dados (LGPD) da empresa está ativada.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {mostraOpcoes && (
            <>
              <hr style={{ borderColor: "var(--border-default)" }} />
              <h6 style={{ fontWeight: 600, fontSize: "0.9rem", color: "var(--text-body)", marginBottom: 4 }}>Opções do campo (tipo Cadastro)</h6>
              <p className="text-muted small">Valores que o usuário poderá escolher ao preencher o documento (ex: Azul, Verde, Amarelo).</p>
              <Table size="sm" hover responsive className="mb-3 align-middle">
                <thead>
                  <tr>
                    <th style={{ fontSize: "var(--text-xs)", textTransform: "uppercase", letterSpacing: "var(--tracking-caps)", color: "var(--table-head-text)", fontWeight: 700, borderTop: "none" }}>Valor</th>
                    <th style={{ width: 80, fontSize: "var(--text-xs)", textTransform: "uppercase", letterSpacing: "var(--tracking-caps)", color: "var(--table-head-text)", fontWeight: 700, borderTop: "none" }}>Ordem</th>
                    <th style={{ width: 100, fontSize: "var(--text-xs)", textTransform: "uppercase", letterSpacing: "var(--tracking-caps)", color: "var(--table-head-text)", fontWeight: 700, borderTop: "none" }}>Situação</th>
                    {!temRespostas && <th style={{ width: 80, textAlign: "center", fontSize: "var(--text-xs)", textTransform: "uppercase", letterSpacing: "var(--tracking-caps)", color: "var(--table-head-text)", fontWeight: 700, borderTop: "none" }}>Ações</th>}
                  </tr>
                </thead>
                <tbody>
                  {opcoes.map((op, idx) => (
                    <tr key={op.id}>
                      <td>
                        {temRespostas ? (
                          op.valor
                        ) : (
                          <Form.Control
                            size="sm"
                            defaultValue={op.valor}
                            onBlur={(e) => {
                              const v = String(e.target.value || "").trim();
                              if (v !== (op.valor || "")) handleSaveOpcaoCampo(op.id, { valor: v, ordem: idx + 1, situacao: op.situacao });
                            }}
                          />
                        )}
                      </td>
                      <td>
                        <Button
                          variant="link"
                          className="p-0 me-1"
                          onClick={() => moveOpcao(idx, "up")}
                          disabled={idx === 0 || savingOpcaoId != null}
                          title="Subir"
                        >
                          <FaArrowUp />
                        </Button>
                        <Button
                          variant="link"
                          className="p-0"
                          onClick={() => moveOpcao(idx, "down")}
                          disabled={idx === opcoes.length - 1 || savingOpcaoId != null}
                          title="Descer"
                        >
                          <FaArrowDown />
                        </Button>
                      </td>
                      <td>
                        <Form.Check
                          type="switch"
                          size="sm"
                          checked={op.situacao === "A"}
                          disabled={savingOpcaoId === op.id}
                          onChange={() => {
                            const nova = op.situacao === "A" ? "I" : "A";
                            handleSaveOpcaoCampo(op.id, { valor: op.valor, ordem: idx + 1, situacao: nova });
                          }}
                        />
                      </td>
                      {!temRespostas && (
                        <td className="text-center">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="p-1"
                            onClick={() => handleExcluirOpcao(op.id)}
                            title="Excluir"
                          >
                            <FaTrash size={14} />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Row className="g-2 align-items-end">
                <Col md={4}>
                  <Form.Label className="small">Novo valor</Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="Ex: Azul"
                    value={opcaoNova.valor}
                    onChange={(e) => setOpcaoNova((p) => ({ ...p, valor: e.target.value }))}
                  />
                </Col>
                <Col md={2}>
                  <Form.Label className="small">Ordem</Form.Label>
                  <Form.Control
                    type="number"
                    size="sm"
                    value={opcaoNova.ordem}
                    onChange={(e) => setOpcaoNova((p) => ({ ...p, ordem: Number(e.target.value) || 0 }))}
                  />
                </Col>
                <Col md={2}>
                  <Form.Label className="small">Situação</Form.Label>
                  <Form.Select
                    size="sm"
                    value={opcaoNova.situacao}
                    onChange={(e) => setOpcaoNova((p) => ({ ...p, situacao: e.target.value }))}
                  >
                    <option value="A">Ativo</option>
                    <option value="I">Inativo</option>
                  </Form.Select>
                </Col>
                <Col md={4}>
                  <Button size="sm" variant="outline-primary" onClick={handleAddOpcao}>Adicionar opção</Button>
                </Col>
              </Row>
            </>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer className="modal-footer-padrao">
        <div className="modal-footer-acoes-esquerda" />
        <div className="modal-footer-acoes-direita">
          <Button variant="light" className="modal-footer-btn-cancelar" onClick={handleClose}>
            Cancelar
          </Button>
          <Button variant="success" className="modal-footer-btn-salvar" onClick={handleSaveCampo} disabled={saving} title="Salvar">
            {saving ? <Spinner animation="border" size="sm" /> : <FaSave size={18} />}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
    {showTipoDocumentoModal && (
      <TiposDocumentoModal
        show={showTipoDocumentoModal}
        handleClose={handleCloseTipoDocumentoModal}
        documento={null}
        idEntidade={entidadeId}
        fetchTiposDocumento={() => carregarTiposDocumento()}
      />
    )}
    </>
  );
};

export default CamposCustomizadosModal;
